import type { OrderWithRelations } from '@snootsvet/shared'

/** An invoice group — orders sharing the same Stripe invoice (paymentId). */
export interface InvoiceGroup {
  paymentId: string
  orders: OrderWithRelations[]
  /** Total across all orders in this invoice, in smallest currency unit. */
  total: number
  /** Latest dispensing date, falling back to order creation. */
  date: string
}

/**
 * Groups paid orders by paymentId, sorted by latest dispensing date descending.
 * Orders in this history generally have dispensedAt; migrated records can lack it,
 * so their creation date provides a fallback.
 */
export function groupByInvoice(orders: OrderWithRelations[]): InvoiceGroup[] {
  const map = Map.groupBy(
    orders.filter((o) => o.paymentId),
    (o) => o.paymentId!
  )

  return [...map.entries()]
    .map(([paymentId, groupOrders]) => ({
      paymentId,
      orders: groupOrders,
      total: groupOrders.reduce((sum, o) => sum + o.totalPrice, 0),
      date: groupOrders.reduce((latest, order) => {
        const date = order.dispensedAt ?? order.createdAt
        return date > latest ? date : latest
      }, groupOrders[0].dispensedAt ?? groupOrders[0].createdAt),
    }))
    .sort((a, b) => b.date.localeCompare(a.date))
}
